<template>
  <div class="mt-5">
    <b-container fluid>
      <div class="card mb-3">
        <div class="card-body py-2 px-3">
          <div class="row align-items-end">
            <div class="col-md-4">
              <div class="form-group m-0 p-0">
                <label>Select Session</label>
                <select
                  class="form-control form-control-sm"
                  v-model="session"
                  @change="loadStats"
                >
                  <option value="">Current Session</option>
                  <option v-for="(s, index) in sessionOptions" :key="index">
                    {{ s }}
                  </option>
                </select>
              </div>
            </div>
            <div class="col-md-2">
              <button class="btn btn-primary btn-sm" @click="loadStats">
                <i class="fas fa-sync-alt"></i> Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <b-overlay :show="loading" rounded="sm">
        <div class="row mb-3">
          <div class="col-md-3">
            <div class="small-box bg-success">
              <div class="inner">
                <h4>{{ formatCurrency(totalCollected) }}</h4>
                <p>Total Collected</p>
              </div>
              <div class="icon"><i class="fas fa-money-bill-wave"></i></div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="small-box bg-warning">
              <div class="inner">
                <h4>{{ formatCurrency(totalPending) }}</h4>
                <p>Total Pending</p>
              </div>
              <div class="icon"><i class="fas fa-clock"></i></div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="small-box bg-info">
              <div class="inner">
                <h4>{{ successCount }}</h4>
                <p>Successful Transactions</p>
              </div>
              <div class="icon"><i class="fas fa-check-circle"></i></div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="small-box bg-danger">
              <div class="inner">
                <h4>{{ pendingCount }}</h4>
                <p>Pending Transactions</p>
              </div>
              <div class="icon"><i class="fas fa-exclamation-circle"></i></div>
            </div>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-md-6">
            <div class="card">
              <div class="card-header"><strong>Collections by Payment Type</strong></div>
              <div class="card-body p-0">
                <b-table
                  :items="byPaymentType"
                  :fields="typeFields"
                  small
                  bordered
                  hover
                  show-empty
                >
                  <template #cell(collected)="{ value }">
                    {{ formatCurrency(value) }}
                  </template>
                </b-table>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="card">
              <div class="card-header"><strong>Collections by Gateway</strong></div>
              <div class="card-body p-0">
                <b-table
                  :items="byGateway"
                  :fields="gatewayFields"
                  small
                  bordered
                  hover
                  show-empty
                >
                  <template #cell(collected)="{ value }">
                    {{ formatCurrency(value) }}
                  </template>
                  <template #cell(gateway)="{ value }">
                    {{ value === 'interswitch' ? 'Interswitch' : 'Remita' }}
                  </template>
                </b-table>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <div class="card">
              <div class="card-header"><strong>Applicant Payments Summary</strong></div>
              <div class="card-body">
                <ul class="list-group list-group-flush">
                  <li class="list-group-item d-flex justify-content-between">
                    <span>Collected</span>
                    <strong class="text-success">{{ formatCurrency(stats.applicant_payments ? stats.applicant_payments.total_collected : 0) }}</strong>
                  </li>
                  <li class="list-group-item d-flex justify-content-between">
                    <span>Pending</span>
                    <strong class="text-warning">{{ formatCurrency(stats.applicant_payments ? stats.applicant_payments.total_pending : 0) }}</strong>
                  </li>
                  <li class="list-group-item d-flex justify-content-between">
                    <span>Total Transactions</span>
                    <strong>{{ stats.applicant_payments ? stats.applicant_payments.total_count : 0 }}</strong>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="card">
              <div class="card-header"><strong>Student Payments Summary</strong></div>
              <div class="card-body">
                <ul class="list-group list-group-flush">
                  <li class="list-group-item d-flex justify-content-between">
                    <span>Collected</span>
                    <strong class="text-success">{{ formatCurrency(stats.student_payments ? stats.student_payments.total_collected : 0) }}</strong>
                  </li>
                  <li class="list-group-item d-flex justify-content-between">
                    <span>Pending</span>
                    <strong class="text-warning">{{ formatCurrency(stats.student_payments ? stats.student_payments.total_pending : 0) }}</strong>
                  </li>
                  <li class="list-group-item d-flex justify-content-between">
                    <span>Total Transactions</span>
                    <strong>{{ stats.student_payments ? stats.student_payments.total_count : 0 }}</strong>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </b-overlay>
    </b-container>
  </div>
</template>

<script>
import { mapState } from "vuex";
export default {
  data() {
    return {
      loading: false,
      session: "",
      stats: {},
      byPaymentType: [],
      byGateway: [],
      typeFields: [
        { key: "type", label: "Payment Type", sortable: true },
        { key: "collected", label: "Amount Collected", sortable: true },
        { key: "count", label: "Transactions", sortable: true },
      ],
      gatewayFields: [
        { key: "gateway", label: "Gateway", sortable: true },
        { key: "source", label: "Source", sortable: true },
        { key: "collected", label: "Amount Collected", sortable: true },
        { key: "count", label: "Transactions", sortable: true },
      ],
    };
  },
  computed: {
    ...mapState({ settings: (state) => state.settings }),
    sessionOptions() {
      if (!this.settings || !this.settings.length) return [];
      return [...new Set(this.settings.map((s) => s.session_name))];
    },
    totalCollected() {
      const a = this.stats.applicant_payments ? Number(this.stats.applicant_payments.total_collected) : 0;
      const s = this.stats.student_payments ? Number(this.stats.student_payments.total_collected) : 0;
      return a + s;
    },
    totalPending() {
      const a = this.stats.applicant_payments ? Number(this.stats.applicant_payments.total_pending) : 0;
      const s = this.stats.student_payments ? Number(this.stats.student_payments.total_pending) : 0;
      return a + s;
    },
    successCount() {
      const a = this.stats.applicant_payments ? Number(this.stats.applicant_payments.success_count) : 0;
      const s = this.stats.student_payments ? Number(this.stats.student_payments.success_count) : 0;
      return a + s;
    },
    pendingCount() {
      const a = this.stats.applicant_payments ? Number(this.stats.applicant_payments.pending_count) : 0;
      const s = this.stats.student_payments ? Number(this.stats.student_payments.pending_count) : 0;
      return a + s;
    },
  },
  created() {
    this.$store.dispatch("getSettings");
    this.loadStats();
  },
  methods: {
    formatCurrency(val) {
      return "₦" + Number(val || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 });
    },
    loadStats() {
      this.loading = true;
      let params = {};
      if (this.session) params.session = this.session;

      axios
        .get("admin/bursary/dashboard-stats", { params })
        .then((response) => {
          this.stats = response.data;
          this.byPaymentType = response.data.by_payment_type || [];

          const applicantGw = (response.data.applicant_by_gateway || []).map((g) => ({
            ...g,
            source: "Applicant",
          }));
          const studentGw = (response.data.student_by_gateway || []).map((g) => ({
            ...g,
            source: "Student",
          }));
          this.byGateway = [...applicantGw, ...studentGw];
        })
        .catch(() => {
          Toast.fire({ icon: "error", title: "Failed to load dashboard stats" });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.small-box {
  border-radius: 0.25rem;
  padding: 20px;
  color: #fff;
  margin-bottom: 10px;
}
.small-box .inner h4 {
  font-size: 1.5rem;
  font-weight: 700;
}
.small-box .inner p {
  margin: 0;
  font-size: 0.9rem;
}
.small-box .icon {
  position: absolute;
  top: 15px;
  right: 15px;
  font-size: 2.5rem;
  opacity: 0.3;
}
</style>
